import { useEffect, useRef, useState } from "react";

interface AppleMap { destroy(): void }
interface MapKitRuntime {
  Coordinate: new (latitude: number, longitude: number) => object;
  Map: new (element: HTMLElement, options: { center: object; cameraDistance: number }) => AppleMap;
}
type MapWindow = Window & { mapkit?: MapKitRuntime; otcLandingMapReady?: () => void };
let mapKitLoading: Promise<MapKitRuntime> | undefined;

function loadAppleMaps() {
  if (mapKitLoading) return mapKitLoading;
  mapKitLoading = (async () => {
    const response = await fetch("/landing-demo/mapkit-config.json");
    if (!response.ok) throw new Error("Map configuration unavailable");
    const config: { token?: string } = await response.json();
    if (!config.token) throw new Error("Map configuration unavailable");
    return new Promise<MapKitRuntime>((resolve, reject) => {
      const mapWindow = window as MapWindow;
      const script = document.createElement("script");
      const timeout = window.setTimeout(() => reject(new Error("Apple Maps unavailable")), 20000);
      mapWindow.otcLandingMapReady = () => {
        window.clearTimeout(timeout);
        if (mapWindow.mapkit) resolve(mapWindow.mapkit);
        else reject(new Error("Apple Maps unavailable"));
      };
      script.src = "https://cdn.apple-mapkit.com/mk/6/mapkit.core.js";
      script.async = true;
      script.dataset.callback = "otcLandingMapReady";
      script.dataset.libraries = "map,services,annotations";
      script.dataset.token = config.token;
      script.onerror = () => {
        window.clearTimeout(timeout);
        reject(new Error("Apple Maps unavailable"));
      };
      document.head.appendChild(script);
    });
  })();
  return mapKitLoading;
}

export function LandingAppleMap() {
  const container = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<"loading" | "ready" | "unavailable">("loading");
  useEffect(() => {
    let disposed = false;
    let map: AppleMap | undefined;
    loadAppleMaps().then((mapkit) => {
      if (disposed || !container.current) return;
      map = new mapkit.Map(container.current, {
        center: new mapkit.Coordinate(32.08, 34.78),
        cameraDistance: 20000,
      });
      setState("ready");
    }).catch(() => { if (!disposed) setState("unavailable"); });
    return () => { disposed = true; map?.destroy(); };
  }, []);
  return (
    <div className="landing-apple-map" aria-label="Apple Maps">
      <div className="landing-apple-map-canvas" ref={container} />
      {state !== "ready" && <p className="landing-map-status" role="status">{state === "loading" ? "Loading Apple Maps…" : <a href="https://maps.apple.com/" target="_blank" rel="noreferrer">Open Apple Maps ↗</a>}</p>}
    </div>
  );
}
